/**
 * Drago Bundle Selector
 * "Make it a kit" add-on: the roller is the base product; an optional
 * checkbox adds Self-Locking Hand Wraps. Unchecked (default) = roller only.
 * Wrap discount handled by the Shopify "50% off with Roller" automatic discount.
 */
class BundleSelector extends HTMLElement {
  constructor() {
    super();
    this.rollerVariant = parseInt(this.dataset.rollerVariant, 10);
    this.wrapsVariant = parseInt(this.dataset.wrapsVariant, 10);
    this.cta = this.querySelector('[data-bundle-add]');
    this.buyNow = this.querySelector('[data-bundle-buy-now]');
    this.ctaText = this.querySelector('.bundle-selector__cta-text');
    this.ctaLoading = this.querySelector('.bundle-selector__cta-loading');
    this.errorEl = this.querySelector('[data-bundle-error]');
    this.addonToggle = this.querySelector('.bundle-option__radio');
    this.ctaPrice = this.querySelector('[data-cta-price]');
  }

  connectedCallback() {
    // Default: add-on unchecked -> Add to Cart adds the roller only.
    this.cta.addEventListener('click', () => this.handleAddToCart(false));
    if (this.buyNow) {
      this.buyNow.addEventListener('click', () => this.handleAddToCart(true));
    }
    if (this.addonToggle) {
      this.addonToggle.addEventListener('change', () => this.updateCtaPrice());
    }
    this.updateCtaPrice();
  }

  updateCtaPrice() {
    if (!this.ctaPrice) return;
    const kitOn = this.addonToggle && this.addonToggle.checked;
    const price = kitOn ? this.dataset.priceKit : this.dataset.priceRoller;
    if (price) this.ctaPrice.textContent = price;
  }

  getSelectedKit() {
    return this.addonToggle && this.addonToggle.checked ? 'roller-1wrap' : 'roller';
  }

  buildItems(kit) {
    switch (kit) {
      case 'roller':
        return [{ id: this.rollerVariant, quantity: 1 }];
      case 'roller-1wrap':
        return [
          { id: this.rollerVariant, quantity: 1 },
          { id: this.wrapsVariant, quantity: 1 }
        ];
      case 'roller-2wraps':
        return [
          { id: this.rollerVariant, quantity: 1 },
          { id: this.wrapsVariant, quantity: 2 }
        ];
      default:
        return [{ id: this.rollerVariant, quantity: 1 }];
    }
  }

  setLoading(loading) {
    this.cta.disabled = loading;
    if (this.buyNow) this.buyNow.disabled = loading;
    this.ctaText.hidden = loading;
    this.ctaLoading.hidden = !loading;
  }

  showError(message) {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  async handleAddToCart(goToCheckout) {
    this.hideError();
    this.setLoading(true);

    const kit = this.getSelectedKit();
    let items = this.buildItems(kit);

    // BUY NOW must not stack a second roller. A customer who already added the
    // roller (or built a cart via the upsells) and then taps Buy Now expects to
    // check out, not add another roller. So on Buy Now we inspect the current cart
    // and drop the roller from the add payload if it's already there, keeping only
    // a missing wrap add-on. If nothing is left to add, go straight to checkout.
    if (goToCheckout) {
      try {
        const current = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const hasRoller = current.items.some((i) => i.id === this.rollerVariant || i.sku === 'DR');
        if (hasRoller) {
          if (kit === 'roller-1wrap') {
            const hasWrap = current.items.some((i) => i.id === this.wrapsVariant);
            items = hasWrap ? [] : [{ id: this.wrapsVariant, quantity: 1 }];
          } else {
            items = [];
          }
        }
      } catch (e) {
        // If the cart lookup fails, fall back to the original add behaviour.
      }
      if (!items.length) {
        window.location.href = '/checkout';
        return;
      }
    }

    // Use the theme's cart drawer for rendering (same pattern as product-form.js)
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const cartAddUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;

    try {
      // Single POST: add the kit item(s) in one request. Shopify's automatic
      // discount engine re-evaluates the cart after the mutation, so the
      // 50%-off-wraps bundle still triggers correctly.
      const body = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      const resp = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
        body: JSON.stringify(body)
      });
      const finalData = await resp.json();
      if (!resp.ok || finalData.status) {
        throw new Error(finalData.description || finalData.message || 'Could not add kit to cart');
      }

      if (goToCheckout) {
        window.location.href = '/checkout';
        return;
      }

      // Let the theme's cart drawer handle the render (proper pattern)
      if (cart && typeof cart.renderContents === 'function') {
        if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(finalData);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('Bundle add to cart failed:', e);
      this.showError(e.message || 'Something went wrong. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}

// cachebust:20260708-2205
